import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SmartTrackingService } from '../core/services/smart-tracking.service';
import { PromotionService } from '../core/services/promotion.service';
import { EnabledOriginCountriesService } from '../core/services/enabled-origin-countries.service';
import { ProductionTranslationService } from '../core/services/production-translation.service';
import { ReferenceDataService } from '../core/services/reference-data.service';
import { warehouseAddressView, WarehouseAddressView } from '../core/utils/warehouse-address.util';
import { Observable } from 'rxjs';
import { Promotion } from '../core/models/promotion.models';
import { environment } from '../environment';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SharedModule } from '../shared/shared.module';
import { MarketingBannerComponent } from '../shared/components/marketing-banner/marketing-banner.component';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.css',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule, SharedModule, MarketingBannerComponent]
})
export class HomeComponent implements OnInit{
  homeForm! : FormGroup;
  homepagePromotions$!: Observable<Promotion[]>;

  // Feature flag pour Virtual Mailbox (visible sur local/dev/acc, caché en prod)
  showVirtualMailbox = environment.featureFlags?.showVirtualMailbox ?? false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private smartTracking: SmartTrackingService,
    private promotionService: PromotionService,
    private readonly enabledOriginCountries: EnabledOriginCountriesService,
    private readonly referenceData: ReferenceDataService,
    private readonly translation: ProductionTranslationService
  ){
    // 🔧 FIX NG01050: Initialiser le FormGroup dans le constructor
    this.homeForm = this.fb.group({
      trackingID: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // 🆕 Load homepage promotions
    this.homepagePromotions$ = this.promotionService.getHomepagePromotions();
  }

  /**
   * #1560 — LE NOMBRE DE PAYS D'ORIGINE, DÉRIVÉ DE SA SOURCE.
   *
   * La page d'accueil annonce une couverture géographique. Ce nombre est un
   * paramètre d'interpolation, jamais un mot du texte traduit : écrit à la
   * main, il serait juste le jour de la publication et faux au premier pays
   * ouvert — et personne ne pense à la page d'accueil en ouvrant un pays
   * (SE + AT, #954, juillet 2026).
   *
   * La règle de repli `reference_data/enabled_countries` → défauts du code
   * n'est PAS réimplémentée ici : elle vit en un seul endroit depuis #956, et
   * ce service en est l'unique porte. Le repli y est volontairement OUVERT —
   * document absent (le cas nominal : il n'existe ni en production ni en
   * acceptance), la page annonce donc les 9 pays d'`ENABLED_ORIGIN_COUNTRIES`,
   * exactement ceux que le tunnel accepte.
   *
   * Lecture synchrone servie par le cache mémoire du référentiel : aucun
   * aller-retour réseau n'est ajouté au premier rendu de l'accueil.
   */
  get openOriginCountries(): { count: number } {
    return { count: this.enabledOriginCountries.getCodesSync().length };
  }

  /**
   * #1561 — L'ADRESSE DE L'ENTREPÔT, DÉRIVÉE DE `reference_data/warehouse_address`.
   *
   * Le bloc affichait quatre lignes inventées écrites en clair dans le
   * template. Publier la vraie rue à côté d'un code destinataire fictif serait
   * pire que le mock : le visiteur qui recopie le bloc expédie un colis réel
   * vers Tubize sous un code qui n'appartient à personne, et il atterrit dans
   * la file des colis non identifiés. La vraie adresse vient donc d'ici, et le
   * code personnel reste un gabarit (`HOME.TOOLS_VM.ADDRESS_NAME`), attribué à
   * l'inscription.
   *
   * Défauts codés en repli : `reference_data` n'a qu'UN document en
   * production, ce sont eux qui gouvernent — c'est le cas nominal, pas le cas
   * dégradé. Déménager l'entrepôt se fait dans `reference_data`, sans toucher
   * ni au code ni aux trois catalogues de langue.
   *
   * #1570 — la MISE EN FORME est partagée avec la maquette d'adresse de
   * `vm/info`, qui montre la même adresse : elle vit dans
   * `core/utils/warehouse-address.util.ts`, pour que les deux pages ne puissent
   * pas diverger. Le nom du pays y est traduit ; le reste vient du référentiel.
   */
  get warehouseAddress(): WarehouseAddressView {
    return warehouseAddressView(this.referenceData.getWarehouseAddressSync(), this.translation);
  }

  onSubmit(): void {
    if (this.homeForm.valid){
      const rawInput = this.homeForm.get("trackingID")?.value;
      
      // Utiliser le système intelligent de détection
      const detection = this.smartTracking.detectTrackingFormat(rawInput);
      const normalizedTrackingID = this.smartTracking.normalizeForSearch(detection);
      
      console.log("Raw input:", rawInput);
      console.log("Smart tracking detection:", detection);
      console.log("Normalized tracking ID:", normalizedTrackingID);
      
      this.homeForm.get('trackingID')?.setValue("");
      this.router.navigate(['/details'], { queryParams: { trackingID: normalizedTrackingID } });
    }
  }

  /**
   * Copie le code promo dynamique dans le presse-papier
   */
  copyPromoCodeDynamic(code: string, index: number): void {
    navigator.clipboard.writeText(code).then(() => {
      // Afficher un feedback visuel sur le bon bouton
      const buttons = document.querySelectorAll('.btn-copy-code');
      const button = buttons[index] as HTMLButtonElement;
      if (button) {
        const originalText = button.innerHTML;
        button.innerHTML = '<i class="fas fa-check me-2"></i>Copié !';
        button.classList.add('copied');

        setTimeout(() => {
          button.innerHTML = originalText;
          button.classList.remove('copied');
        }, 2000);
      }
    }).catch(err => {
      console.error('Erreur lors de la copie:', err);
    });
  }
}
